import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import { useCurrentProject } from '@adsk/alloy-provider';
import { useLocale } from '@adsk/alloy-react-helpers';
import {
  useAnalytics,
  useCreateAccountCompany,
  useCreateAccountCompanyValidation,
} from '@common/hooks';
import {
  COMPONENT_VARIANTS,
  OP_STATUSES,
  TRACK_EVENTS,
} from '@common/constants';
import { getTrades } from '@common/utils';
import { FLAG_NAMES } from '@common/flagNames';
import {
  useAdminFeatureFlag,
  withAdminFFs,
} from '@common/AdminLaunchDarklyProvider';
import { getNewCompanyEventProperties } from './utils';
import { useCreateProjectCompany, useGetCompanies } from './hooks';
import AddCompanyModalView from './component';

const {
  ADMIN_FLAGS: { ACC_ENABLE_ADD_COMPANY_DEBOUNCE },
} = FLAG_NAMES;

const initialAddress = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  country: '',
  phone: '',
  postalCode: '',
  stateOrProvince: '',
  type: 'Main',
};

export const AddCompanyModal = ({
  isOpen,
  onClose,
  onSubmit,
  variant,
  ...rest
}) => {
  const { showNotification } = useNotifications();
  const { trackEvent } = useAnalytics();
  const canCreate = useCreateAccountCompanyValidation();
  const selectOnly = !canCreate;
  const currentProject = useCurrentProject();
  const isAccountAdmin = currentProject?.accessLevels?.accountAdministrator;
  const isProjectAdmin = currentProject?.accessLevels?.projectAdministrator;
  const { T, locale: localeName } = useLocale(({ locale }) =>
    require(`./i18n/${locale}.json`)
  );
  const trades = useMemo(() => getTrades(localeName), [localeName]);
  const { value: enableAddCompanyDebounce } = useAdminFeatureFlag(
    ACC_ENABLE_ADD_COMPANY_DEBOUNCE
  );
  const { companies, setCompanies, onChangeCompanyInput } = useGetCompanies(
    enableAddCompanyDebounce,
    variant
  );
  const latestOnChangeCompanyInput = useRef(onChangeCompanyInput);
  const { resetForm, setFieldValue, setValues, values } = rest;
  const { selectedCompany } = values;

  const handleAddProjectCompanySuccess = (addedCompany) => {
    showNotification({
      status: NOTIFICATION_STATUSES.SUCCESS,
      message: T.translate('MODAL_SUCCESS_NOTIFICATION', {
        company: addedCompany.name,
      }),
    });
    setCompanies(
      companies.map((company) => {
        if (company.id === addedCompany.id)
          return { ...company, disabled: true };
        return company;
      })
    );
    if (onSubmit) onSubmit({ status: OP_STATUSES.SUCCESS });
    resetForm();
  };

  const handleError = () => {
    showNotification({
      status: NOTIFICATION_STATUSES.ERROR,
      message: T.translate('MODAL_ERROR_NOTIFICATION', {
        company: selectedCompany.name,
      }),
    });
    if (onSubmit) onSubmit({ status: OP_STATUSES.ERROR });
    setFieldValue('isSaving', false);
  };

  const { createProjectCompanyRequest } = useCreateProjectCompany({
    onSuccess: handleAddProjectCompanySuccess,
    onError: handleError,
  });

  const handleCreateAccountCompanySuccess = (companyId) => {
    showNotification({
      status: NOTIFICATION_STATUSES.SUCCESS,
      message: T.translate('MODAL_SUCCESS_CREATE_NOTIFICATION', {
        company: values.name,
      }),
    });
    setCompanies([
      ...companies,
      { id: companyId, name: values.name, disabled: true },
    ]);

    if (variant === COMPONENT_VARIANTS.ACCOUNT) {
      if (onSubmit) onSubmit({ status: OP_STATUSES.SUCCESS });
      resetForm();
    } else {
      createProjectCompanyRequest({ companyId });
    }
  };

  const { createAccountCompanyRequest } = useCreateAccountCompany({
    onSuccess: handleCreateAccountCompanySuccess,
    onError: handleError,
  });

  const sendAnalyticsAfterAddCompany = () => {
    if (variant === COMPONENT_VARIANTS.ACCOUNT) {
      trackEvent(
        `${TRACK_EVENTS.SCOPE.ACCOUNT_ADMIN}.${TRACK_EVENTS.NAME.COMPANY}.${TRACK_EVENTS.NAME.CREATE}`,
        { origin: 'companies' }
      );
      trackEvent(
        `${TRACK_EVENTS.SCOPE.ACCOUNT_ADMIN}.${TRACK_EVENTS.SOURCE.COMPANIES_MODAL}.${TRACK_EVENTS.NAME.ADD}.${TRACK_EVENTS.NAME.COMPLETE}`,
        getNewCompanyEventProperties(values)
      );
    } else {
      trackEvent(
        `${TRACK_EVENTS.SCOPE.PROJECT_ADMIN}.${TRACK_EVENTS.NAME.COMPANY}.${TRACK_EVENTS.NAME.CREATE}`,
        { origin: 'companies' }
      );
      trackEvent(
        `${TRACK_EVENTS.SCOPE.PROJECT_ADMIN}.${TRACK_EVENTS.SOURCE.COMPANIES_MODAL}.${TRACK_EVENTS.NAME.ADD}.${TRACK_EVENTS.NAME.COMPLETE}`,
        {
          account_admin: isAccountAdmin,
          project_admin: isProjectAdmin,
          ...getNewCompanyEventProperties(values),
        }
      );
    }
  };

  const handleSubmit = () => {
    setFieldValue('isSaving', true);
    sendAnalyticsAfterAddCompany();

    if (
      variant === COMPONENT_VARIANTS.ACCOUNT ||
      (variant === COMPONENT_VARIANTS.PROJECT &&
        canCreate &&
        values.selectedCompany.id === 1)
    ) {
      createAccountCompanyRequest(
        values.name,
        values.trade?.value,
        values.addresses,
        values.website,
        values.description,
        values.erp,
        values.tax
      );
    } else if (variant === COMPONENT_VARIANTS.PROJECT) {
      createProjectCompanyRequest({ companyId: values.selectedCompany.id });
    }
  };

  const handleCancel = () => {
    if (variant === COMPONENT_VARIANTS.ACCOUNT) {
      trackEvent(
        `${TRACK_EVENTS.SCOPE.ACCOUNT_ADMIN}.${TRACK_EVENTS.SOURCE.COMPANIES_MODAL}.${TRACK_EVENTS.NAME.ADD}.${TRACK_EVENTS.NAME.CANCEL}`,
        getNewCompanyEventProperties(values)
      );
    } else {
      trackEvent(
        `${TRACK_EVENTS.SCOPE.PROJECT_ADMIN}.${TRACK_EVENTS.SOURCE.COMPANIES_MODAL}.${TRACK_EVENTS.NAME.ADD}.${TRACK_EVENTS.NAME.CANCEL}`,
        {
          account_admin: isAccountAdmin,
          project_admin: isProjectAdmin,
          ...getNewCompanyEventProperties(values),
        }
      );
    }
    resetForm();
    onClose();
  };

  const handleCompanyChange = (company) => {
    setValues({
      ...values,
      addresses: company.addresses,
      description: company.description || T.translate('NO_DESCRIPTION'),
      erp: company.erpId || T.translate('NO_ERP'),
      name: company.name,
      nameInput: '',
      selectedCompany: company,
      tax: company.taxId || T.translate('NO_TAX'),
      trade:
        trades.find((trade) => trade.value === company.trade) ||
        trades.find((trade) => trade.value === 'Other'),
      website: company.websiteUrl || T.translate('NO_WEBSITE'),
    });
  };

  const handleCreateCompany = (name, stateAndHelpers) => {
    const isExistingCompanySelected = companies.some((company) => {
      return company.name.toLowerCase() === name.toLowerCase();
    });

    if (!isExistingCompanySelected) {
      const company = {
        id: 1,
        name,
        disabled: false,
      };
      setValues({
        ...values,
        addresses: [{}],
        description: '',
        erp: '',
        name: company.name,
        nameInput: '',
        selectedCompany: company,
        tax: '',
        trade: null,
        website: '',
      });
      stateAndHelpers?.closeMenu();
    }
  };

  const handleCloseCompanyDropdown = () => {
    if (values.nameInput === '') return;

    setFieldValue('nameInput', '');
  };

  const handleCompanyDropdownStateChange = (changes, stateAndHelpers) => {
    if (
      values.nameInput !== '' &&
      (changes.type === '__autocomplete_blur_input__' ||
        changes.type === '__autocomplete_mouseup__' ||
        changes.type === 10 ||
        changes.type === 1)
    ) {
      handleCreateCompany(values.nameInput, stateAndHelpers);
    }
  };

  const handleAddAddressClick = () => {
    setFieldValue('addresses', [...values.addresses, {}]);
  };

  const handleAddressReset = (index) => {
    const currentCountry = values.addresses[index].country;
    setFieldValue(`addresses[${index}]`, initialAddress);
    setFieldValue(`addresses[${index}].country`, currentCountry);
  };

  useEffect(() => {
    latestOnChangeCompanyInput.current = onChangeCompanyInput;
  }, [onChangeCompanyInput]);

  useEffect(() => {
    latestOnChangeCompanyInput.current(values.nameInput);
  }, [values.nameInput]);

  return (
    <AddCompanyModalView
      {...rest}
      companies={companies}
      isOpen={isOpen}
      onAddAddressClick={handleAddAddressClick}
      onAddressReset={handleAddressReset}
      onCancel={handleCancel}
      onCloseCompanyDropdown={handleCloseCompanyDropdown}
      onCloseModal={onClose}
      onCompanyChange={handleCompanyChange}
      onCompanyDropdownStateChange={handleCompanyDropdownStateChange}
      onCreateCompany={handleCreateCompany}
      onSubmit={handleSubmit}
      selectOnly={selectOnly}
      trades={trades}
      variant={variant}
    />
  );
};

AddCompanyModal.propTypes = {
  isOpen: PropTypes.bool,
  mapBoxAccessToken: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  variant: PropTypes.oneOf(Object.values(COMPONENT_VARIANTS)),
};

AddCompanyModal.defaultProps = {
  isOpen: false,
  mapBoxAccessToken: undefined,
  onClose: null,
  onSubmit: null,
  variant: COMPONENT_VARIANTS.PROJECT,
};

const AddCompanyModalWithFormik = withFormik({
  mapPropsToValues: () => ({
    addresses: [initialAddress],
    description: '',
    erp: '',
    isSaving: false,
    name: null,
    nameInput: '',
    selectedCompany: null,
    tax: '',
    trade: null,
    website: '',
  }),
  displayName: 'AddCompanyModal',
})(AddCompanyModal);

AddCompanyModalWithFormik.propTypes = AddCompanyModal.propTypes;

export default withAdminFFs(AddCompanyModalWithFormik);
